import { Add, UploadFile } from "@mui/icons-material";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import type { SkillsNewMenuButtonProps } from "../types";

const NEW_MENU_UNMOUNT_DELAY_MS = 100;

export const SkillsNewMenuButton = ({
  onCreateSkill,
  onUploadSkill,
}: SkillsNewMenuButtonProps) => {
  const [newMenuAnchorEl, setNewMenuAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [isNewMenuMounted, setIsNewMenuMounted] = useState(false);
  const newMenuUnmountTimerRef = useRef<number | null>(null);
  const isNewMenuOpen = Boolean(newMenuAnchorEl);

  const cancelNewMenuUnmount = () => {
    if (newMenuUnmountTimerRef.current === null) {
      return;
    }

    window.clearTimeout(newMenuUnmountTimerRef.current);
    newMenuUnmountTimerRef.current = null;
  };

  const scheduleNewMenuUnmount = () => {
    cancelNewMenuUnmount();

    newMenuUnmountTimerRef.current = window.setTimeout(() => {
      newMenuUnmountTimerRef.current = null;
      setIsNewMenuMounted(false);
    }, NEW_MENU_UNMOUNT_DELAY_MS);
  };

  const closeNewMenu = () => {
    setNewMenuAnchorEl(null);
    scheduleNewMenuUnmount();
  };

  const handleNewMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    cancelNewMenuUnmount();
    setIsNewMenuMounted(true);
    setNewMenuAnchorEl(event.currentTarget);
  };

  const handleCreateFromNewMenu = () => {
    closeNewMenu();
    onCreateSkill();
  };

  const handleUploadFromNewMenu = () => {
    closeNewMenu();
    onUploadSkill();
  };

  useEffect(
    () => () => {
      if (newMenuUnmountTimerRef.current !== null) {
        window.clearTimeout(newMenuUnmountTimerRef.current);
      }
    },
    []
  );

  return (
    <>
      <Tooltip title="New skill">
        <IconButton
          id="skills-new-button"
          size="small"
          aria-controls={isNewMenuOpen ? "skills-new-menu" : undefined}
          aria-haspopup="menu"
          aria-expanded={isNewMenuOpen ? true : undefined}
          aria-label="New skill"
          edge="end"
          onClick={handleNewMenuOpen}
          sx={{
            backgroundColor: isNewMenuOpen
              ? "rgba(7, 78, 232, 0.08)"
              : "transparent",
            color: isNewMenuOpen ? "primary.main" : "text.secondary",
          }}
        >
          <Add fontSize="small" />
        </IconButton>
      </Tooltip>
      {isNewMenuMounted && (
        <Menu
          id="skills-new-menu"
          anchorEl={newMenuAnchorEl}
          open={isNewMenuOpen}
          onClose={closeNewMenu}
          transitionDuration={NEW_MENU_UNMOUNT_DELAY_MS}
          MenuListProps={{
            "aria-labelledby": "skills-new-button",
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handleCreateFromNewMenu}>
            <Add fontSize="small" sx={{ mr: 1 }} />
            Create skill
          </MenuItem>
          <MenuItem onClick={handleUploadFromNewMenu}>
            <UploadFile fontSize="small" sx={{ mr: 1 }} />
            Upload .md file
          </MenuItem>
        </Menu>
      )}
    </>
  );
};
